// MCHD Room Scheduler - Building List Component
// Displays buildings in a table with actions

import React from 'react';
import { Card, Badge, ActionIcon, Menu, Text, Group, Avatar, Skeleton, Button, SimpleGrid, Stack, Box } from '@mantine/core';
import { IconDots, IconEdit, IconTrash, IconBuilding, IconBuildingStore, IconEye, IconCalendar, IconHome } from '@tabler/icons-react';

interface Building {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  floorCount?: number;
  roomCount?: number;
  holidaysCount?: number;
  createdAt: string;
  updatedAt: string;
}

interface BuildingListProps {
  buildings: Building[];
  loading: boolean;
  onEdit: (building: Building) => void;
  onDelete: (buildingId: string) => void;
  onSelect: (building: Building) => void;
}

/**
 * Building List Component
 * Displays buildings in a table with action buttons
 */
export const BuildingList: React.FC<BuildingListProps> = ({
  buildings,
  loading,
  onEdit,
  onDelete,
  onSelect
}) => {
  /**
   * Format date for display
   */
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Get building initials for avatar
   */
  const getBuildingInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <SimpleGrid cols={{ base: 1, sm: 2, xl: 3 }} spacing="md" p="lg">
        {Array.from({ length: 6 }).map((_, index) => (
          <Card key={index} shadow="sm" radius="md" p="lg">
            <Stack gap="md">
              <Group gap="md">
                <Skeleton height={48} circle />
                <div style={{ flex: 1 }}>
                  <Skeleton height={20} width="80%" mb="xs" />
                  <Skeleton height={14} width="60%" />
                </div>
              </Group>
              
              <Group gap="sm" justify="center">
                <Skeleton height={60} width={60} radius="md" />
                <Skeleton height={60} width={60} radius="md" />
                <Skeleton height={60} width={60} radius="md" />
              </Group>
              
              <Group justify="space-between">
                <Skeleton height={24} width={80} />
                <Skeleton height={32} width={32} circle />
              </Group>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    );
  }

  if (buildings.length === 0) {
    return (
      <Stack align="center" gap="lg" py="xl">
        <IconBuilding size={64} color="var(--mantine-color-gray-4)" />
        <Text size="lg" fw={500} c="dimmed">No buildings found</Text>
        <Text size="sm" c="dimmed" ta="center">
          Get started by creating your first building
        </Text>
      </Stack>
    );
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, xl: 3 }} spacing="md" p="lg">
      {buildings.map((building) => (
        <Card 
          key={building.id}
          shadow="sm" 
          radius="md" 
          p={0}
          style={{
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            border: '1px solid transparent'
          }}
          className="building-card"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04)';
            e.currentTarget.style.borderColor = '#28A8E0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)';
            e.currentTarget.style.borderColor = 'transparent';
          }}
        >
          {/* Gradient Header */}
          <Box
            p="lg"
            style={{
              background: 'linear-gradient(135deg, #28A8E0 0%, #1e8bc3 100%)',
              borderRadius: '8px 8px 0 0',
              color: 'white'
            }}
          >
            <Group gap="md" align="flex-start">
              <Avatar 
                size="lg" 
                radius="md"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.3)'
                }}
              >
                {getBuildingInitials(building.name)}
              </Avatar>
              <div style={{ flex: 1 }}>
                <Text size="lg" fw={700} c="white">
                  {building.name}
                </Text>
                {building.description && (
                  <Text size="sm" c="rgba(255, 255, 255, 0.8)" mt={4}>
                    {building.description}
                  </Text>
                )}
              </div>
            </Group>
          </Box>

          {/* Stats Section */}
          <Box p="lg">
            <Group gap="sm" justify="center" mb="md">
              {/* Floors Stat */}
              <Box
                ta="center"
                p="md"
                style={{
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  minWidth: '70px'
                }}
              >
                <IconBuildingStore size={20} color="#28A8E0" />
                <Text size="lg" fw={700} c="dark" mt="xs">
                  {building.floorCount || 0}
                </Text>
                <Text size="xs" c="dimmed">
                  Floors
                </Text>
              </Box>

              {/* Rooms Stat */}
              <Box
                ta="center"
                p="md"
                style={{
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  minWidth: '70px'
                }}
              >
                <IconHome size={20} color="#28A8E0" />
                <Text size="lg" fw={700} c="dark" mt="xs">
                  {building.roomCount || 0}
                </Text>
                <Text size="xs" c="dimmed">
                  Rooms
                </Text>
              </Box>

              {/* Holidays Stat */}
              <Box
                ta="center"
                p="md"
                style={{
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  minWidth: '70px'
                }}
              >
                <IconCalendar size={20} color="#28A8E0" />
                <Text size="lg" fw={700} c="dark" mt="xs">
                  {building.holidaysCount || 0}
                </Text>
                <Text size="xs" c="dimmed">
                  Holidays
                </Text>
              </Box>
            </Group>

            {/* Status and Actions */}
            <Group justify="space-between" align="center">
              <Badge
                color={building.isActive ? 'green' : 'red'}
                variant="light"
                size="sm"
              >
                {building.isActive ? 'Active' : 'Inactive'}
              </Badge>
              
              <Group gap="xs">
                <Button
                  size="sm"
                  leftSection={<IconEye size={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(building);
                  }}
                  style={{
                    backgroundColor: '#28A8E0',
                    border: 'none'
                  }}
                >
                  Manage
                </Button>
                
                <Menu shadow="md" width={200}>
                  <Menu.Target>
                    <ActionIcon 
                      variant="subtle" 
                      c="gray"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconDots size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconEdit size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(building);
                      }}
                    >
                      Edit Building
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      leftSection={<IconTrash size={14} />}
                      c="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(building.id);
                      }}
                    >
                      Delete Building
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>

            {/* Created Date */}
            <Text size="xs" c="dimmed" mt="sm">
              Created: {formatDate(building.createdAt)}
            </Text>
          </Box>
        </Card>
      ))}
    </SimpleGrid>
  );
};
